import { useEffect, useRef } from 'react';

import {
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  type SelectChangeEvent,
} from '@mui/material';
import { styled } from '@mui/material/styles';

import { gql } from '@apollo/client';
import { useMutation, useQuery, useReactiveVar } from '@apollo/client/react';

import { startInteraction } from '@common/sentry/helpers';
import { transientOptions } from '@common/themes/styles/styled';

import type {
  ActivateScenarioMutation,
  ActivateScenarioMutationVariables,
  ScenariosQuery,
} from '@/common/__generated__/graphql';
import { activeScenarioVar } from '@/common/cache';
import { useTranslation } from '@/common/i18n';
import { useInstance } from '@/common/instance';
import type { SiteContextScenario } from '@/context/site';
import { GET_SCENARIOS } from '@/queries/getScenarios';

const ACTIVATE_SCENARIO = gql`
  mutation ActivateScenario($scenarioId: ID!) {
    activateScenario(id: $scenarioId) {
      ok
      activeScenario {
        id
        name
        isActive
        isDefault
        isSelectable
      }
    }
  }
`;

const StyledFormControl = styled(FormControl)`
  max-width: 320px;
  min-width: 100px;
  width: 100%;
`;

const StyledInputLabel = styled(InputLabel)`
  /* Position label above the input like Bootstrap */
  position: static;
  transform: none;
  color: ${(props) => props.theme.palette.text.primary};
  font-size: ${(props) => props.theme.fontSizeSm};

  /* Remove MUI's shrink behavior */
  &.MuiInputLabel-shrink {
    transform: none;
  }

  /* Override focused state */
  &.Mui-focused {
    color: ${(props) => props.theme.palette.text.primary};
  }
`;

const StyledSelect = styled(Select<string>, transientOptions)<{ $custom: boolean }>`
  /* Make it look like Bootstrap form-control */
  .MuiSelect-select {
    padding: 8px 12px;
    font-size: 1rem;
    line-height: 1.5;
    background-color: ${(props) =>
      props.$custom ? props.theme.graphColors.yellow010 : props.theme.inputBg};
  }
`;

const StyledMenuItem = styled(MenuItem)<{ $custom?: boolean }>`
  background-color: ${(props) =>
    props.$custom ? props.theme.graphColors.yellow010 : 'transparent'};
`;
StyledMenuItem.displayName = 'StyledMenuItem';

const isCustomScenario = (scenario: { id: string }) => {
  return scenario.id === 'custom';
};

export default function ScenarioSelector(props: { testId?: string }) {
  const { testId: testIdBase } = props;
  const { t } = useTranslation();
  const instance = useInstance();
  const activeScenario = useReactiveVar(activeScenarioVar);

  const selectId = 'scenario-select';
  const labelId = 'scenario-select-label';

  const { loading, error, data, previousData } = useQuery<ScenariosQuery>(GET_SCENARIOS, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    context: {
      componentName: 'ScenarioSelector',
    },
  });

  const testId = testIdBase ? `${testIdBase}-selector` : undefined;

  const [activateScenario, { loading: mutationLoading, error: mutationError }] = useMutation<
    ActivateScenarioMutation,
    ActivateScenarioMutationVariables
  >(ACTIVATE_SCENARIO, {
    refetchQueries: 'active',
    onCompleted: (dat) => {
      const newScenario = dat.activateScenario?.activeScenario;
      // We  want to update activeScenarioVar only in onCompleted mutations
      if (newScenario) {
        activeScenarioVar({
          ...newScenario,
          kind: null,
          actualHistoricalYears: null,
          isUserSelected: true,
        });
      }
    },
  });

  // Reconcile the activeScenarioVar with the fresh client-side GET_SCENARIOS
  // result. The SSR-initialized var can be stale (the RSC Apollo client
  // doesn't propagate the user's session cookie, so the backend returns
  // default-as-active in SSR). This is a one-time hydration fix: run it only
  // for the first settled client-side result. After that, scenario changes are
  // owned by the mutations (ACTIVATE_SCENARIO here and SET_PARAMETER in
  // ParameterWidget). Continuing to reconcile would race those mutations and
  // stomp a just-set "custom" scenario back to "default" whenever a refetch
  // observes pre-commit backend state (read-after-write lag, worse under CI
  // load).
  const hasReconciledRef = useRef(false);
  useEffect(() => {
    if (hasReconciledRef.current) return;
    if (loading || mutationLoading || !data) return;
    const backendActive = data.scenarios.find((s) => s.isActive);
    if (!backendActive) return;
    hasReconciledRef.current = true;
    if (activeScenario?.id === backendActive.id) return;
    activeScenarioVar({
      ...backendActive,
      kind: null,
      actualHistoricalYears: null,
      isUserSelected: false,
    });
  }, [data, loading, mutationLoading, activeScenario?.id]);

  if (loading && !previousData) {
    return (
      <StyledFormControl data-testid={testId}>
        <StyledInputLabel id={labelId} htmlFor={selectId}>
          {t('plot-scenario')}
        </StyledInputLabel>
        <StyledSelect
          value={t('loading')}
          id={selectId}
          labelId={labelId}
          name="scenario"
          $custom={false}
        >
          <MenuItem disabled value={t('loading')}>
            <span>
              <CircularProgress size={16} />
            </span>
          </MenuItem>
        </StyledSelect>
      </StyledFormControl>
    );
  }
  if (error || mutationError) {
    //console.log("Error", JSON.stringify(error));
    return <div>{t('error-loading-data')}</div>;
  }

  const hideBaseScenario = instance.features?.baselineVisibleInGraphs === false;
  const scenarios =
    data?.scenarios.filter(
      (scen) => scen.isSelectable && (hideBaseScenario ? scen.id !== 'baseline' : true)
    ) ?? [];

  const handleChange = (event: SelectChangeEvent<string>) => {
    void startInteraction(
      () => activateScenario({ variables: { scenarioId: event.target.value } }),
      {
        name: 'activateScenario',
        componentName: 'ScenarioSelector',
        attributes: { scenario_id: event.target.value },
      }
    );
  };

  return (
    <StyledFormControl disabled={loading || mutationLoading} data-testid={testId}>
      <StyledInputLabel id={labelId} htmlFor={selectId}>
        {t('plot-scenario')}
      </StyledInputLabel>
      <StyledSelect
        value={activeScenario.id}
        onChange={handleChange}
        id={selectId}
        labelId={labelId}
        name="scenario"
        $custom={isCustomScenario(activeScenario)}
      >
        <MenuItem disabled value="">
          {t('change-scenario')}
        </MenuItem>
        {scenarios.map((scenario) => (
          <StyledMenuItem key={scenario.id} value={scenario.id}>
            {isCustomScenario(scenario as unknown as SiteContextScenario) ? (
              <i>{scenario.name}</i>
            ) : (
              scenario.name
            )}
          </StyledMenuItem>
        ))}
      </StyledSelect>
    </StyledFormControl>
  );
}
